import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const demoAccessGuide: AdminGuide = {
  id: "demo-access",
  title: "Grant or revoke demo access (Demo Company)",
  category: "Demo & Partner",
  audience: ["Support", "Ops", "CS"],
  summary:
    "Enable or disable demo-company access for a specific user, without touching real customer data.",
  keywords: [
    "demo",
    "demo access",
    "demo company",
    "firebase uid",
    "training",
    "preview",
    "production",
  ],
  location: "Admin",
  risk: "medium",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.DEMO_COMPANY,
    GUIDE_SOURCE_DOCS.ADMIN_OVERVIEW,
  ],
  warnings: [
    "Granting demo access to the wrong user can create confusion and may expose demo-only data to someone who should not see it.",
    "Prefer the user’s Firebase UID (`firebaseId`) as the identifier. Email is a convenience lookup and can be ambiguous (aliases, shared inboxes).",
  ],
  safetyNotes: [
    "Demo mode uses example Demo Company data. It does not show or change a customer's real organisation data.",
    "If someone shares demo screenshots, label them as demo screenshots so they are not mistaken for real financials.",
  ],
  whatThisHelpsYouDo: [
    "Grant demo-company access to a specific user for training or a guided demo.",
    "Revoke demo-company access when it is no longer needed.",
    "Confirm the user can enter and exit demo mode safely.",
  ],
  whenToUse: [
    "Internal training or a guided product demo.",
    "A customer explicitly requests a demo experience and you have approval to enable it.",
    "You are validating demo-mode behaviour (Preview or Production) with a controlled user.",
  ],
  whenNotToUse: [
    "You need to reproduce a real customer issue in their real org data (use spoofing with permission).",
    "You cannot confirm the correct user identity and environment.",
  ],
  beforeYouStart: [
    "A ticket/case reference (who requested this and why).",
    "User identifier: Firebase UID (`firebaseId`) preferred; email as fallback.",
    "Confirm the environment that matters for the request. A local developer setup cannot confirm Preview or Production access.",
  ],
  steps: [
    "Open Admin → **Demo Access Management** (`/admin/demo-access`).",
    "Click **Grant Demo Access**.",
    "Select the identifier type: **Firebase UID** (preferred) or **Email**.",
    "Paste the identifier carefully and double-check it before submitting.",
    "Confirm the user appears in the list and shows **Enabled**.",
    "Ask the user to log out and log back in (or refresh their org selector).",
    "Confirm the user can enter demo mode and can exit demo mode back to their real org (if they have one).",
    "When demo access is no longer needed, revoke it from the same page.",
  ],
  checklists: [
    {
      title: "Safe grant checklist",
      items: [
        "I have a ticket/case reference and a clear reason for granting access.",
        "I confirmed the environment (Preview vs Production).",
        "I used Firebase UID (`firebaseId`) where possible.",
        "I verified the user is the intended person before granting access.",
      ],
    },
    {
      title: "After the demo",
      items: [
        "I revoked demo access if it was temporary.",
        "I recorded what I changed (granted/revoked + timestamp) in the ticket/case.",
      ],
    },
  ],
  whatSuccessLooksLike: [
    "The correct user shows **Enabled** in Demo Access Management.",
    "The user can enter demo mode and exit demo mode without getting stuck.",
    "Demo access is revoked when it is no longer needed (if temporary).",
  ],
  troubleshooting: [
    {
      title: "Grant/revoke fails or the list won’t load",
      body: [
        "Refresh the page and try again.",
        "If this only happens locally, ask Engineering to check the local Demo Access setup. Use Preview or Production checks for Preview or Production access issues.",
        "Escalate with the time of the attempt and the Firebase UID (do not include any tokens).",
      ],
    },
    {
      title: "User still can’t see demo mode after access is enabled",
      body: [
        "Ask the user to log out and log back in (demo eligibility is loaded during app boot).",
        "Confirm you granted access to the correct Firebase UID.",
        "If it’s still missing, escalate and ask Engineering to confirm demo mode is enabled in that environment.",
      ],
    },
  ],
  escalateWhen: [
    "You cannot confirm the correct Firebase UID for the intended user.",
    "Demo Access Management fails to load or grant/revoke fails repeatedly.",
    "The user has demo access enabled but demo mode does not appear after a fresh login.",
    "The user gets stuck in demo mode and cannot exit back to their real org.",
  ],
  relatedGuides: [
    { label: "Demo Ops diagnostics", guideId: "demo-ops-diagnostics" },
    {
      label: "Spoofing (impersonation) safely",
      guideId: "spoofing-safely",
    },
    { label: "Onboarding support", guideId: "onboarding-support" },
  ],
  relatedAdminPaths: [
    { label: "Demo Access Management", path: "/admin/demo-access" },
    { label: "Demo Ops", path: "/admin/demo-ops" },
  ],
};
